import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { base_url } from "Backend/api";
import toast from "react-hot-toast";
import MarketHomeCard from "./MarketHomeCard";
import { enrichMarketsWithOrdersCount } from "utils/enrichMarketsWithOrdersCount";
import AppSpinner from "components/ui/AppSpinner";
import FavoriteToggleDialog, {
  useFavoriteToggleDialog,
} from "components/favorites/FavoriteToggleDialog";
import {
  emitFavoriteToggled,
  subscribeFavoriteToggled,
} from "utils/favoriteEvents";

function listFromResponse(json) {
  if (!json) return [];
  if (Array.isArray(json)) return json;
  if (json.status && Array.isArray(json.data)) return json.data;
  if (Array.isArray(json.data)) return json.data;
  return [];
}

const HomeMarketsFeedSection = ({ sectionId, title, feedKey }) => {
  const navigate = useNavigate();
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState({});
  const { showDialog, dialogMessage, showFavoriteDialog } =
    useFavoriteToggleDialog();

  const fetchList = useCallback(
    async (lat, lng, { showLoader = true } = {}) => {
      if (showLoader) setLoading(true);
      try {
        const token = localStorage.getItem("token");
        let url = `${base_url}/api/home/markets/${feedKey}`;
        const latStr = lat != null ? String(lat).trim() : "";
        const lngStr = lng != null ? String(lng).trim() : "";
        if (latStr && lngStr) {
          url += `?latitude=${encodeURIComponent(latStr)}&longitude=${encodeURIComponent(lngStr)}`;
        }
        const response = await fetch(url, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            Accept: "application/json",
          },
        });
        const result = await response.json();
        const list = listFromResponse(result);
        const enriched = await enrichMarketsWithOrdersCount(list, token);
        setMarkets(enriched);
        const fav = {};
        list.forEach((m) => {
          if (m?.slug) fav[m.slug] = m.is_favorite || false;
        });
        setFavorites(fav);
      } catch (error) {
        console.error(error);
        setMarkets([]);
      } finally {
        if (showLoader) setLoading(false);
      }
    },
    [feedKey]
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      await fetchList("", "", { showLoader: true });
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return;
          fetchList(pos.coords.latitude, pos.coords.longitude, { showLoader: false });
        },
        () => {},
        { enableHighAccuracy: false, timeout: 12000, maximumAge: 300000 }
      );
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [fetchList]);

  useEffect(() => {
    return subscribeFavoriteToggled(({ storeType, slug, isFavorite }) => {
      if (storeType !== "market" || !slug) return;

      setFavorites((prev) => ({
        ...prev,
        [slug]: !!isFavorite,
      }));

      setMarkets((prev) =>
        prev.map((market) =>
          market?.slug === slug
            ? { ...market, is_favorite: !!isFavorite }
            : market
        )
      );
    });
  }, []);

  const handleMarketClick = (slug) => navigate(`/market/${slug}`);

  const handleToggleFavorite = async (slug) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${base_url}/api/favorites/markets/${slug}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (result.status) {
        const newState =
          typeof result?.data?.is_favorite === "boolean"
            ? result.data.is_favorite
            : !favorites[slug];
        setFavorites((prev) => ({ ...prev, [slug]: newState }));
        setMarkets((prev) =>
          prev.map((market) =>
            market?.slug === slug
              ? { ...market, is_favorite: newState }
              : market
          )
        );
        emitFavoriteToggled({
          storeType: "market",
          slug,
          isFavorite: newState,
        });
        showFavoriteDialog(newState);
      } else {
        toast.error(result.message || "حدث خطأ");
      }
    } catch (error) {
      console.error(error);
      toast.error("حدث خطأ في الشبكة");
    }
  };

  if (loading) {
    return (
      <section id={sectionId} className="w-full px-4 md:px-6 lg:px-8 py-2 scroll-mt-32">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-[14px] md:text-[16px] lg:text-[18px] font-bold text-foreground mb-2 font-[cairo]">
            {title}
          </h2>
          <div className="flex justify-center py-6">
            <AppSpinner />
          </div>
        </div>
      </section>
    );
  }

  if (markets.length === 0) return null;

  return (
    <>
    <section id={sectionId} className="w-full px-4 md:px-6 lg:px-8 py-2 scroll-mt-32">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-[14px] md:text-[16px] lg:text-[18px] font-bold text-foreground mb-2 font-[cairo]">
          {title}
        </h2>

        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={6}
          slidesPerView={2.1}
          breakpoints={{ 640: { slidesPerView: 2.5 },
           768: { slidesPerView: 2.5 },
            1024: { slidesPerView: 3.5 } }}
          dir="rtl"
        >
          {markets.map((market) => (
            <SwiperSlide key={market.id ?? market.slug}>
              <MarketHomeCard
                market={market}
                isFavorite={!!favorites[market.slug]}
                onCardClick={handleMarketClick}
                onFavoriteClick={handleToggleFavorite}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
    <FavoriteToggleDialog open={showDialog} message={dialogMessage} />
    </>
  );
};

export default HomeMarketsFeedSection;
